import { resolveProductImage } from "./productAssets";

const CART_KEY = "bobbinusa_cart";
export const CART_UPDATED_EVENT = "bobbinusa_cart_updated";
const CHECKOUT_KEY = "bobbinusa_checkout";
const CONFIRMED_ORDER_KEY = "bobbinusa_last_order";

export const fallbackCartItem = {
  name: "New Tension Complete Singer 20U Zig Zag",
  seller: "Bobbinusa.com",
  sku: "S20U-TENSION-COMP",
  condition: "New",
  price: 17.95,
  quantity: 1,
  image: "/uploads/catalog/hotproducts/1.png",
};

export function formatMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value || 0));
}

export function productToCartItem(product, quantity = 1, variant = null, selectedOptions = {}) {
  const variantPrice = Number(variant?.price || 0) > 0 ? variant.price : product?.price;
  const variantSku = variant?.sku || product?.sku || fallbackCartItem.sku;

  return {
    productId: product?._id,
    variantId: variant?._id,
    variantName: variant?.name,
    selectedOptions: selectedOptions || variant?.attributes || {},
    name: product?.name || fallbackCartItem.name,
    seller: "Bobbinusa.com",
    sku: variantSku,
    condition: product?.condition || "New",
    price: Number(variantPrice || fallbackCartItem.price),
    quantity: Math.max(1, Number(quantity || 1)),
    image: variant?.image || product?.image || fallbackCartItem.image,
    weightLb: Number(variant?.weightLb ?? product?.weightLb ?? 1),
  };
}

export function getCartItemKey(item = {}) {
  return [
    item.productId || "",
    item.variantId || "",
    item.sku || "",
    JSON.stringify(item.selectedOptions || {}),
  ].join("::");
}

function safeParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

export function readCartItems() {
  const stored = safeParse(localStorage.getItem(CART_KEY), []);
  if (Array.isArray(stored)) return stored.filter(Boolean);
  return stored ? [stored] : [];
}

export function readCartItem() {
  return readCartItems()[0] || null;
}

export function writeCartItems(items = []) {
  const normalized = (Array.isArray(items) ? items : [items])
    .filter(Boolean)
    .map((item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity || 1)),
    }));
  localStorage.setItem(CART_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new Event(CART_UPDATED_EVENT));
}

export function writeCartItem(item) {
  writeCartItems(item ? [item] : []);
}

export function addCartItem(item) {
  if (!item) return [];
  const current = readCartItems();
  const nextItem = {
    ...item,
    quantity: Math.max(1, Number(item.quantity || 1)),
  };
  const itemKey = getCartItemKey(nextItem);
  let merged = false;
  const next = current.map((cartItem) => {
    if (getCartItemKey(cartItem) !== itemKey) return cartItem;
    merged = true;
    return {
      ...cartItem,
      quantity: Number(cartItem.quantity || 1) + nextItem.quantity,
    };
  });

  if (!merged) next.push(nextItem);
  writeCartItems(next);
  return next;
}

export function clearCartItem() {
  localStorage.removeItem(CART_KEY);
  window.dispatchEvent(new Event(CART_UPDATED_EVENT));
}

export function getCartImage(item) {
  return resolveProductImage(item?.image || fallbackCartItem.image);
}

export function calculateSummary(itemOrItems, shipping = 0, discountPercent = 0) {
  const items = Array.isArray(itemOrItems) ? itemOrItems : itemOrItems ? [itemOrItems] : [];
  const subtotal = Number(
    items
      .reduce((sum, item) => sum + Number(item?.price || 0) * Number(item?.quantity || 1), 0)
      .toFixed(2)
  );
  const safeDiscountPercent = Math.max(0, Math.min(100, Number(discountPercent || 0)));
  const discount = Number(((subtotal * safeDiscountPercent) / 100).toFixed(2));
  const taxableSubtotal = Math.max(0, subtotal - discount);
  const tax = Number((taxableSubtotal * 0.08).toFixed(2));
  const total = Number((taxableSubtotal + Number(shipping || 0) + tax).toFixed(2));
  return { subtotal, discount, discountPercent: safeDiscountPercent, shipping: Number(shipping || 0), tax, total };
}

export function writeCheckoutDraft(draft) {
  localStorage.setItem(CHECKOUT_KEY, JSON.stringify(draft));
}

export function readCheckoutDraft() {
  return safeParse(localStorage.getItem(CHECKOUT_KEY), null);
}

export function writeConfirmedOrder(order) {
  localStorage.setItem(CONFIRMED_ORDER_KEY, JSON.stringify(order));
}

export function readConfirmedOrder() {
  return safeParse(localStorage.getItem(CONFIRMED_ORDER_KEY), null);
}
